/**
 * This context houses all filters and their selected values.
 * The filters themselves are not responsible for storing their own state. They read and write their values using exposed methods from this context.
 *
 * A draft and final system is used to allow for immediate user feedback with selections without replacing the url until they close this filter component.
 * When the filter component is blurred, the draft will be finalized.
 *
 *
 */

import { ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
    CheckboxFilterItem,
    FilterItem,
    FilterItemType,
    FilterType,
    FinalizedQuery,
    NumericFilterItem,
    SelectFilterItem,
    TextFilterItem,
    ToggleFilterItem,
    UpdateCheckboxFilterParams,
    UpdateFilterItemParams,
    UpdateNumericFilterItem,
    UpdateSelectFilterItem,
    UpdateTextFilterParams,
    UpdateToggleFilterItem,
} from './filterV2.types';
import { useSearchParams } from 'react-router-dom';
import { clearDateSearch, clearNumericSearch, clearTextSearch } from './utils';
import * as qs from 'qs';

type FilterV2ContextType = {
    filters: Map<string, FilterItem>;
    register: (filter: FilterItem) => void;
    getDraft: <T extends FilterType = FilterType>(id: string, type?: T) => FilterItemType<T>;
    getAll: () => FilterItem[];
    updateValues: <T extends FilterType>(type: T, id: string, params: UpdateFilterItemParams<T>, finalize?: boolean) => void;
    finalize: (_draft?: Map<string, FilterItem>) => void;
    clear: (id?: string, clearQuery?: boolean) => Map<string, FilterItem>;
    focusFilter: (id?: string) => void;
    focusedFilter: string | undefined;
    currentSearch: URLSearchParams;
    setCurrentSearch: React.Dispatch<React.SetStateAction<URLSearchParams>>;
    query: {
        query: FinalizedQuery;
        queryString: string;
    };
};

const FilterV2Context = createContext<FilterV2ContextType>({
    filters: new Map(),
    register: () => undefined,
    getDraft: () => undefined,
    getAll: () => undefined,
    updateValues: () => undefined,
    finalize: (_draft?: Map<string, FilterItem>) => undefined,
    clear: () => undefined,
    focusFilter: () => undefined,
    focusedFilter: undefined,
    currentSearch: new URLSearchParams(),
    setCurrentSearch: () => undefined,
    query: { query: {}, queryString: '' },
});

type FilterV2ProviderProps = {
    children: ReactNode;
};

function FilterV2Provider({ children }: FilterV2ProviderProps): JSX.Element {
    const [filters, setFilters] = useState<Map<string, FilterItem>>(new Map());
    const [currentSearch, setCurrentSearch] = useState(new URLSearchParams());
    const [draft, setDraft] = useState<Map<string, FilterItem>>(new Map());
    const [focusedFilter, setFocusedFilter] = useState<string>();
    const [search, setSearch] = useSearchParams();
    const [query, setQuery] = useState<{ query: FinalizedQuery; queryString: string }>({ query: {}, queryString: '' });

    /**
     * Adds a new filter to the Map.
     *
     * @param id
     * @param label
     */
    const register = useCallback(
        (filter: FilterItem) => {
            if (filters.get(filter.id)) {
                return;
            }

            setFilters((f) => {
                f.set(filter.id, filter);
                return new Map(f);
            });
            setDraft((f) => {
                f.set(filter.id, filter);
                return new Map(f);
            });
        },
        [filters]
    );

    /**
     * Copy the state of the draft into the actual filter record.
     */
    const finalize = useCallback(
        (_draft?: Map<string, FilterItem>) => {
            const copy = _draft || draft;
            // console.log(copy);
            // Remove any falsy values
            const clean = new Map<string, FilterItem>();

            copy.forEach((c) => {
                if (c.type === 'checkbox') {
                    clean.set(c.id, c);
                } else if (c.type === 'text') {
                    // Remove any blank values
                    clean.set(c.id, { ...c, values: c.values.map((v) => v.trim()).filter((v) => v) });
                } else if (c.type === 'numeric' || c.type === 'date') {
                    // Remove any non-numeric values
                    clean.set(c.id, { ...c, values: c.values.filter((v) => typeof v === 'number') });
                } else if (c.type === 'toggle') {
                    clean.set(c.id, { ...c, active: c.active });
                } else if (c.type === 'select') {
                    clean.set(c.id, { ...c, values: c.values });
                }
            });

            clean.forEach((c) => {
                switch (c.type) {
                    case 'checkbox':
                        search.delete(c.id);
                        c.values.forEach((v) => search.append(c.id, v));

                        break;
                    case 'text': {
                        clearTextSearch(c.id, search);
                        const id = `${c.id}[${c.condition}]`;
                        c.values.forEach((v) => search.append(id, v));
                        break;
                    }
                    case 'numeric': {
                        clearNumericSearch(c.id, search);
                        if (c.values.length >= 1) {
                            // The between condition is just an alias for gte & lte
                            if (c.condition === 'btw') {
                                const [val1, val2] = c.values;
                                if (typeof val1 === 'number' && typeof val2 === 'number') {
                                    search.append(`${c.id}[gte]`, c.values[0].toString());
                                    search.append(`${c.id}[lte]`, c.values[1].toString());
                                }
                            } else {
                                const id = `${c.id}[${c.condition}]`;
                                search.append(id, c.values[0].toString());
                            }
                        }
                        break;
                    }
                    case 'date': {
                        clearDateSearch(c.id, search);
                        if (c.values.length >= 1) {
                            // The between condition is just an alias for gt & lt
                            if (c.condition === 'btw') {
                                const [val1, val2] = c.values;
                                if (typeof val1 === 'number' && typeof val2 === 'number') {
                                    search.append(`${c.id}[gt]`, c.values[0].toString());
                                    search.append(`${c.id}[lt]`, c.values[1].toString());
                                }
                            } else {
                                const id = `${c.id}[${c.condition}]`;
                                search.append(id, c.values[0].toString());
                            }
                        }
                        break;
                    }
                    case 'toggle': {
                        search.delete(c.id);
                        if (c.active) {
                            search.append(c.id, c.active.toString());
                        }
                        break;
                    }
                    case 'select': {
                        search.delete(c.id);
                        c.values.forEach((v) => search.append(c.id, v.value.toString()));
                        break;
                    }
                    default:
                        break;
                }
            });

            setCurrentSearch(search);
            setSearch(search, { replace: true });
            const map = new Map(clean);
            setFilters(map);
            setDraft(map);
            formatQuery(map);
            setFocusedFilter('');
        },
        [draft, search, setSearch]
    );

    /**
     * Updates a filter's selection
     */
    const updateValues = useCallback(
        function update<T extends FilterType>(type: T, id: string, params: UpdateFilterItemParams<T>, finalizeUpdate = false) {
            const draftCopy = new Map(draft);

            switch (type) {
                case 'checkbox': {
                    const _params = params as UpdateCheckboxFilterParams;
                    const copy = { ...draftCopy.get(id) } as CheckboxFilterItem;
                    draftCopy.set(id, { ...copy, values: _params.values });
                    break;
                }
                case 'text': {
                    const _params = params as UpdateTextFilterParams;

                    const copy = { ...draftCopy.get(id) } as TextFilterItem;

                    if (_params.condition) {
                        copy.condition = _params.condition;
                    }
                    if (_params.values) {
                        copy.values = _params.values;
                    }

                    draftCopy.set(id, copy);
                    break;
                }
                case 'numeric': {
                    const _params = params as UpdateNumericFilterItem;

                    const copy = { ...draftCopy.get(id) } as NumericFilterItem;

                    if (_params.condition) {
                        copy.condition = _params.condition;
                    }
                    if (_params.values) {
                        copy.values = _params.values;
                    }

                    draftCopy.set(id, copy);
                    break;
                }
                case 'toggle': {
                    const _params = params as UpdateToggleFilterItem;

                    const copy = { ...draftCopy.get(id) } as ToggleFilterItem;

                    if (_params.active) {
                        copy.active = true;
                    } else {
                        copy.active = undefined;
                    }

                    draftCopy.set(id, copy);
                    break;
                }
                case 'select': {
                    const _params = params as UpdateSelectFilterItem;
                    const copy = { ...draftCopy.get(id) } as SelectFilterItem;
                    draftCopy.set(id, { ...copy, values: _params.values });
                    break;
                }

                default:
                    break;
            }

            setDraft(draftCopy);
            if (finalizeUpdate) {
                finalize(draftCopy);
            }
        },
        [draft, finalize]
    );

    /**
     * Get a specific filter in it's draft state
     */
    const getDraft = useCallback(
        function get<T extends FilterType = FilterType>(id: string, type?: T): FilterItemType<T> {
            return draft.get(id) as FilterItemType<T>;
        },
        [draft]
    );

    /**
     * Get all finalized filters
     */
    const getAll = useCallback(() => {
        return Array.from(filters.values());
    }, [filters]);

    /**
     * Removes a specific filters selection
     */
    const clear = useCallback(
        (id?: string, updateQuery = true): Map<string, FilterItem> => {
            if (id) {
                const d = new Map(draft);
                const item = d.get(id);

                if (item.type === 'toggle') {
                    if (item.active) {
                        d.set(id, { ...item, active: false });
                    }
                } else {
                    if (item.values.length > 0) {
                        d.set(id, { ...item, values: [] });
                    }
                }
                setDraft(d);

                if (updateQuery) {
                    setSearch(
                        (s) => {
                            console.log(s.toString());
                            s.delete(id);
                            return s;
                        },
                        { replace: true }
                    );
                }
                return d;
            } else {
                // TODO: Clear all selected values
                return draft;
            }
        },
        [draft, setSearch]
    );

    /**
     * Instruct the specific filter to automatically open with the user opens the filter menu
     */
    const focusFilter = useCallback((id?: string) => {
        setFocusedFilter(id);
    }, []);

    /**
     * Formats the current filters into a format to send to the API
     * @param filters
     */
    function formatQuery(filters: Map<string, FilterItem>): void {
        const q: FinalizedQuery = {};

        filters.forEach((f) => {
            switch (f.type) {
                case 'checkbox':
                    if (f.values.length) {
                        q[f.id] = f.values;
                    }
                    break;
                case 'text':
                    if (f.values.length) {
                        q[f.id] = {
                            [f.condition]: f.values,
                        };
                    }
                    break;
                case 'numeric':
                    if (f.values.length) {
                        if (f.condition === 'btw') {
                            q[f.id] = {
                                gte: f.values[0].toString(),
                                lte: f.values[1].toString(),
                            };
                        } else {
                            q[f.id] = {
                                [f.condition]: f.values[0].toString(),
                            };
                        }
                    }
                    break;
                case 'date':
                    if (f.values.length) {
                        if (f.condition === 'btw') {
                            q[f.id] = {
                                gt: f.values[0].toString(),
                                lt: f.values[1].toString(),
                            };
                        } else {
                            q[f.id] = {
                                [f.condition]: f.values[0].toString(),
                            };
                        }
                    }
                    break;
                case 'toggle':
                    if (f.active) {
                        q[f.id] = String(f.active);
                    }
                    break;
                case 'select':
                    if (f.values.length) {
                        q[f.id] = f.values.map((v) => v.value.toString());
                    }
                    break;
                default:
                    break;
            }
        });

        setQuery({ query: q, queryString: qs.stringify(q, { indices: false }) });
    }

    const value = useMemo(() => {
        return {
            filters,
            register,
            getDraft,
            getAll,
            updateValues,
            finalize,
            clear,
            focusedFilter,
            focusFilter,
            currentSearch,
            query,
            setCurrentSearch,
        };
    }, [filters, register, getDraft, getAll, updateValues, finalize, clear, focusedFilter, focusFilter, currentSearch, query]);

    return <FilterV2Context.Provider value={value}>{children}</FilterV2Context.Provider>;
}

export function useFilterContext(): FilterV2ContextType {
    const context = useContext(FilterV2Context);
    if (context === undefined) {
        throw new Error('useFilterContext must be used within a FilterV2Provider');
    }
    return context;
}

export default FilterV2Provider;
